import { Paragraph, TextRun, TableRow, AlignmentType, PageOrientation } from "docx";
import * as m from "@app/paraglide/messages.js";
import {
    createHeaderCell,
    createDataCell,
    createEmptyCell,
    createRow,
    formatTimecode,
    formatDuration,
    getEventNames,
    buildContentWithTags,
    paragraphsFromMultilineText,
    SHADING_LIGHT,
    SHADING_DARK
} from "../helpers.js";
import convertToFormattedTextRuns from "../convertToFormattedTextRuns.js";

/**
 * Condensed CCSL template.
 * 8 columns, portrait orientation, single header row with light blue shading.
 * Dialogue events and spotting events are merged using rowSpan.
 * Uses only the main section (no head/metadata/footer).
 */
export default {
    id: "condensed",

    getDocumentConfig() {
        return {
            orientation: PageOrientation.PORTRAIT,
            useSections: false,
            hasPageHeader: true
        };
    },

    /**
     * Returns the header row for the condensed template.
     * @returns {TableRow[]}
     */
    getHeaders() {
        return [new TableRow({
            tableHeader: true,
            children: [
                createHeaderCell(m.ccsl_export_header_sh_number(), AlignmentType.LEFT, SHADING_LIGHT),
                createHeaderCell(m.ccsl_export_header_tc(), AlignmentType.LEFT, SHADING_LIGHT),
                createHeaderCell(m.ccsl_export_header_scene_description(), AlignmentType.LEFT, SHADING_LIGHT),
                createHeaderCell(m.ccsl_export_header_title_number(), AlignmentType.LEFT, SHADING_LIGHT),
                createHeaderCell(m.ccsl_export_header_in(), AlignmentType.LEFT, SHADING_LIGHT),
                createHeaderCell(m.ccsl_export_header_out(), AlignmentType.LEFT, SHADING_LIGHT),
                createHeaderCell(m.ccsl_export_header_duration_short(), AlignmentType.LEFT, SHADING_LIGHT),
                createHeaderCell(m.ccsl_export_header_titles(), AlignmentType.LEFT, SHADING_LIGHT),
            ]
        })];
    },

    /**
     * Builds table rows for a dialogue event with matching spotting events.
     * First 3 columns (shot#, timecode, scene desc) span all spotting rows.
     * @param {object} event - The dialogue event
     * @param {number} index - Event index
     * @param {Array} spottingEvents - Matching spotting events
     * @param {object} spottingEventGroup - Full spotting event group
     * @param {object} project - Project state
     * @returns {TableRow[]}
     */
    buildDialogueRowWithSpotting(event, index, spottingEvents, spottingEventGroup, project) {
        const rows = [];

        spottingEvents.forEach((spottingEvent, spIndex) => {
            const spottingEventIndex = spottingEventGroup.events.findIndex(e => e.id === spottingEvent.id);
            const { speakerNames, recipientNames } = getEventNames(spottingEvent);

            const cells = [];

            // First spotting row includes the dialogue columns with rowSpan
            if (spIndex === 0) {
                cells.push(
                    createDataCell(
                        new Paragraph({
                            children: [new TextRun({ text: (index + 1).toString(), bold: true })],
                            alignment: AlignmentType.LEFT,
                        }),
                        { rowSpan: spottingEvents.length, shading: SHADING_DARK }
                    ),
                    createDataCell(formatTimecode(event.start, project), { rowSpan: spottingEvents.length }),
                    createDataCell(buildContentWithTags(event.tags, [
                        ...paragraphsFromMultilineText(event.notes),
                        new Paragraph(" "),
                        new Paragraph({ children: convertToFormattedTextRuns(event.text) })
                    ]), { rowSpan: spottingEvents.length }),
                );
            }

            // Spotting columns (every row)
            cells.push(
                createDataCell((spottingEventIndex + 1).toString(), { rowSpan: 1 }),
                createDataCell(formatTimecode(spottingEvent.start, project), { rowSpan: 1 }),
                createDataCell(formatTimecode(spottingEvent.end, project), { rowSpan: 1 }),
                createDataCell(formatDuration(spottingEvent.start, spottingEvent.end, project), { rowSpan: 1 }),
                createDataCell(
                    buildContentWithTags(spottingEvent.tags, new Paragraph({ children: convertToFormattedTextRuns(spottingEvent.text, speakerNames, recipientNames) })),
                    { rowSpan: 1 }
                ),
            );

            rows.push(createRow(cells));
        });

        return rows;
    },

    /**
     * Builds a table row for a dialogue event without spotting events.
     * @param {object} event - The dialogue event
     * @param {number} index - Event index
     * @param {object} project - Project state
     * @returns {TableRow[]}
     */
    buildDialogueRowWithoutSpotting(event, index, project) {
        return [createRow([
            createDataCell(
                new Paragraph({
                    children: [new TextRun({ text: (index + 1).toString(), bold: true })],
                    alignment: AlignmentType.LEFT,
                }),
                { shading: SHADING_DARK }
            ),
            createDataCell(formatTimecode(event.start, project)),
            createDataCell(buildContentWithTags(event.tags, [
                ...paragraphsFromMultilineText(event.notes),
                new Paragraph(" "),
                new Paragraph({ children: convertToFormattedTextRuns(event.text) })
            ])),
            createEmptyCell(),
            createEmptyCell(),
            createEmptyCell(),
            createEmptyCell(),
            createEmptyCell(),
        ])];
    }
};
